import React, { useMemo, useState } from 'react';
import { StyleSheet, View, Text, ScrollView, Dimensions, TouchableOpacity, Platform } from 'react-native';
import Svg, { Path, Circle, Line } from 'react-native-svg';
import { JobCard } from '../../types';
import theme from '../../theme';

type DateRange = '1M' | '3M' | '6M' | '1Y' | 'ALL';

interface WorkshopTimeGraphProps {
  jobCards: JobCard[];
}

export const WorkshopTimeGraph = ({ jobCards }: WorkshopTimeGraphProps) => {
  const windowWidth = Dimensions.get('window').width;
  const isWeb = Platform.OS === 'web';
  // Account for all horizontal spacing on web:
  // - Sidebar: 280px
  // - MainContent padding: 24px (left) + 24px (right) = 48px
  // - Screen contentContainer padding: 16px (left) + 16px (right) = 32px
  // - Graph container padding: 16px (left) + 16px (right) = 32px
  // Total: 280 + 48 + 32 + 32 = 392px
  const totalHorizontalSpacing = isWeb ? 392 : 64; // Mobile just has basic padding
  const screenWidth = windowWidth - totalHorizontalSpacing;
  const [dateRange, setDateRange] = useState<DateRange>('3M');
  
  const graphData = useMemo(() => {
    // Build car records with start/end dates (HTML dashboard approach)
    const carRecords = jobCards.map(jc => {
      const start = new Date(jc.requestDate);
      start.setHours(0, 0, 0, 0);
      
      let end: Date | null = null;
      
      // If job has return date, use it as end date (car left workshop)
      if (jc.returnDate) {
        end = new Date(jc.returnDate);
        end.setHours(0, 0, 0, 0);
      } else if (jc.jobStatus === 'completed' || jc.jobStatus === 'handed_over') {
        // If completed but no return date, treat as same-day completion
        end = new Date(jc.requestDate);
        end.setHours(0, 0, 0, 0);
      }
      // If still in_progress and no return date, end remains null (still in workshop)
      
      return { start, end };
    }).filter(rec => rec.start && !isNaN(rec.start.getTime()));
    
    // Determine date range from actual data
    if (carRecords.length === 0) {
      return { days: [], maxCount: 0, startDate: null, endDate: null };
    }
    
    // Find earliest request date and latest delivery date (or today if still in progress)
    const allDates = carRecords.flatMap(rec => {
      const dates = [rec.start];
      if (rec.end) dates.push(rec.end);
      return dates;
    });
    
    const dataMinDate = new Date(Math.min(...allDates.map(d => d.getTime())));
    dataMinDate.setHours(0, 0, 0, 0);
    
    const dataMaxDate = new Date(Math.max(...allDates.map(d => d.getTime())));
    dataMaxDate.setHours(0, 0, 0, 0);
    
    // Calculate start date based on selected range
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let minDate: Date;
    if (dateRange === 'ALL') {
      minDate = dataMinDate;
    } else {
      const rangeStart = new Date(today);
      switch (dateRange) {
        case '1M':
          rangeStart.setMonth(rangeStart.getMonth() - 1);
          break;
        case '3M':
          rangeStart.setMonth(rangeStart.getMonth() - 3);
          break;
        case '6M':
          rangeStart.setMonth(rangeStart.getMonth() - 6);
          break;
        case '1Y':
          rangeStart.setFullYear(rangeStart.getFullYear() - 1);
          break;
      }
      rangeStart.setHours(0, 0, 0, 0);
      // Use the later of range start or data min date
      minDate = rangeStart > dataMinDate ? rangeStart : dataMinDate;
    }
    
    // If latest date is in the past, use it; otherwise use today
    const endDate = dataMaxDate < today ? dataMaxDate : today;
    
    const days = [];
    const currentDate = new Date(minDate);
    
    while (currentDate <= endDate) {
      // Count cars in workshop on this day
      // A car is in workshop if: entered on or before this day AND (not delivered OR delivered after this day)
      // Note: Use > for end date to EXCLUDE the delivery day (car is no longer in workshop on delivery day)
      const count = carRecords.filter(rec => 
        rec.start <= currentDate && (!rec.end || rec.end > currentDate)
      ).length;
      
      days.push({
        date: new Date(currentDate),
        count,
        label: currentDate.getDate().toString(),
      });
      
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    const maxCount = Math.max(...days.map(d => d.count), 1);
    const averageCount = days.length > 0 ? days.reduce((sum, d) => sum + d.count, 0) / days.length : 0;
    
    return { days, maxCount, averageCount, startDate: minDate, endDate };
  }, [jobCards, dateRange]);

  const { days, maxCount, averageCount, startDate, endDate } = graphData;
  
  // Calculate graph dimensions - dynamic spacing to always fill screen
  const graphWidth = screenWidth; // Use full available width after accounting for all spacing
  const graphHeight = 200;
  const paddingVertical = 30;
  const chartHeight = graphHeight - paddingVertical;
  // Dynamic spacing: divide available width by number of points to always fill screen
  // Account for Y-axis width (40px)
  const availableChartWidth = graphWidth - 40;
  const pointSpacing = days.length > 1 ? availableChartWidth / (days.length - 1) : 0;
  
  // Build SVG path for line - moved BEFORE early return to satisfy Rules of Hooks
  const linePath = useMemo(() => {
    if (days.length === 0) return { path: '', points: [] };
    
    const points = days.map((day, index) => {
      const x = index * pointSpacing;
      const y = chartHeight - (maxCount > 0 ? (day.count / maxCount) * chartHeight : 0);
      return { x, y, count: day.count };
    });
    
    const pathData = points.map((point, index) => {
      const command = index === 0 ? 'M' : 'L';
      return `${command}${point.x},${point.y}`;
    }).join(' ');
    
    return { path: pathData, points };
  }, [days, maxCount, pointSpacing, chartHeight]);
  // Early return AFTER all hooks
  if (days.length === 0) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Text style={styles.title}>Cars at Workshop Over Time</Text>
          <Text style={styles.subtitle}>No data available</Text>
        </View>
      </View>
    );
  }
  
  // Calculate average line Y position
  const avgY = chartHeight - (maxCount > 0 ? (averageCount / maxCount) * chartHeight : 0);
  
  const formatDateRange = () => {
    if (!startDate || !endDate) return '';
    const start = startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    const end = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${start} - ${end}`;
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Cars at Workshop Over Time</Text>
        <Text style={styles.subtitle}>{formatDateRange()} ({days.length} days)</Text>
      </View>
      
      {/* Date Range Filter Buttons */}
      <View style={styles.filterContainer}>
        <TouchableOpacity
          style={[styles.filterButton, dateRange === '1M' && styles.filterButtonActive]}
          onPress={() => setDateRange('1M')}
        >
          <Text style={[styles.filterButtonText, dateRange === '1M' && styles.filterButtonTextActive]}>
            1M
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.filterButton, dateRange === '3M' && styles.filterButtonActive]}
          onPress={() => setDateRange('3M')}
        >
          <Text style={[styles.filterButtonText, dateRange === '3M' && styles.filterButtonTextActive]}>
            3M
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.filterButton, dateRange === '6M' && styles.filterButtonActive]}
          onPress={() => setDateRange('6M')}
        >
          <Text style={[styles.filterButtonText, dateRange === '6M' && styles.filterButtonTextActive]}>
            6M
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.filterButton, dateRange === '1Y' && styles.filterButtonActive]}
          onPress={() => setDateRange('1Y')}
        >
          <Text style={[styles.filterButtonText, dateRange === '1Y' && styles.filterButtonTextActive]}>
            1Y
          </Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.filterButton, dateRange === 'ALL' && styles.filterButtonActive]}
          onPress={() => setDateRange('ALL')}
        >
          <Text style={[styles.filterButtonText, dateRange === 'ALL' && styles.filterButtonTextActive]}>
            ALL
          </Text>
        </TouchableOpacity>
      </View>
      
      <View style={styles.graphContainer}>
        {/* Y-axis labels */}
        <View style={styles.yAxis}>
          <Text style={styles.yAxisLabel}>{maxCount}</Text>
          <Text style={styles.yAxisLabel}>{Math.floor(maxCount / 2)}</Text>
          <Text style={styles.yAxisLabel}>0</Text>
        </View>
        
        {/* Line Graph */}
        <View style={styles.chartContainer}>
          <Svg width={availableChartWidth} height={graphHeight}>
            {/* Grid lines */}
            <Line
              x1="0"
              y1={chartHeight}
              x2={availableChartWidth}
              y2={chartHeight}
              stroke={theme.colors.gray300}
              strokeWidth="1"
            />
            <Line
              x1="0"
              y1={chartHeight / 2}
              x2={availableChartWidth}
              y2={chartHeight / 2}
              stroke={theme.colors.gray200}
              strokeWidth="1"
              strokeDasharray="4,4"
            />
            
            {/* Average line */}
            <Line
              x1="0"
              y1={avgY}
              x2={availableChartWidth}
              y2={avgY}
              stroke={theme.colors.warning}
              strokeWidth="2"
              strokeDasharray="8,4"
            />
            
            {/* Line path */}
            <Path
              d={linePath.path}
              stroke={theme.colors.primary}
              strokeWidth="3"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            
            {/* Data points */}
            {linePath.points.map((point, index) => (
              <Circle
                key={index}
                cx={point.x}
                cy={point.y}
                r="4"
                fill={theme.colors.primary}
                stroke={theme.colors.white}
                strokeWidth="2"
              />
            ))}
          </Svg>
          
          {/* X-axis labels */}
          <View style={styles.xAxisLabels}>
            {days.map((day, index) => {
              // Show labels for every 7th day or first/last
              const showLabel = index === 0 || index === days.length - 1 || index % 7 === 0;
              if (!showLabel) return <View key={index} style={{ width: pointSpacing }} />;
              
              return (
                <View key={index} style={[styles.xLabelWrapper, { width: pointSpacing }]}>
                  <Text style={styles.xAxisLabel}>
                    {day.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </Text>
                </View>
              );
            })}
          </View>
        </View>
      </View>
      
      <View style={styles.footer}>
        <View style={styles.legendContainer}>
          <View style={styles.legendItem}>
            <View style={[styles.legendLine, { backgroundColor: theme.colors.primary }]} />
            <Text style={styles.legendText}>Workshop Count</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendLine, { backgroundColor: theme.colors.warning, height: 2 }]} />
            <Text style={styles.legendText}>Average ({averageCount.toFixed(1)})</Text>
          </View>
        </View>
        <Text style={styles.statsText}>
          Current: {jobCards.filter(jc => jc.jobStatus !== 'completed' && jc.jobStatus !== 'handed_over').length} cars • Peak: {maxCount} cars
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    padding: theme.spacing.md,
    marginVertical: theme.spacing.sm,
    ...theme.shadows.md,
  },
  header: {
    marginBottom: theme.spacing.md,
  },
  title: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '700' as const,
    color: theme.colors.textPrimary,
  },
  subtitle: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginTop: 2,
  },
  filterContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
    flexWrap: 'wrap',
  },
  filterButton: {
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.xs,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.gray300,
    backgroundColor: theme.colors.white,
    minWidth: 50,
    alignItems: 'center',
  },
  filterButtonActive: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
  filterButtonText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    fontWeight: '600' as const,
  },
  filterButtonTextActive: {
    color: theme.colors.white,
  },
  graphContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  yAxis: {
    width: 30,
    height: 200,
    justifyContent: 'space-between',
    paddingRight: theme.spacing.xs,
  },
  yAxisLabel: {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.textSecondary,
    textAlign: 'right',
  },
  chartContainer: {
    flex: 1,
  },
  xAxisLabels: {
    flexDirection: 'row',
    marginTop: 8,
    height: 30,
  },
  xLabelWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  xAxisLabel: {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.textSecondary,
    transform: [{ rotate: '-45deg' }],
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing.md,
    paddingTop: theme.spacing.sm,
    borderTopWidth: 1,
    borderTopColor: theme.colors.gray200,
  },
  legendContainer: {
    flexDirection: 'row',
    gap: theme.spacing.md,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.xs,
  },
  legendLine: {
    width: 20,
    height: 3,
    borderRadius: 2,
  },
  legendText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  statsText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textPrimary,
    fontWeight: '600' as const,
  },
});
